import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Keyboard,
    LayoutChangeEvent,
    Pressable,
    StyleSheet,
    TouchableOpacity,
    TouchableWithoutFeedback,
} from 'react-native';

import LockIcon from '@/assets/icons/lock-lg';
import RemoveIcon from '@/assets/icons/remove-sm';
import { InnerBorder } from '@/components/InnerBorder';
import { LineSeparator } from '@/components/LineSeparator';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { GASbkEvents } from '@/feature/analytics/sbk/ga-sbk-events';
import { BonusToggleRow } from '@/feature/betslip-sbk/components/BonusToggle';
import {
    useIsBetSlipSubmitting,
    useIsOptionSuspended,
    useShowBonusToggle,
} from '@/feature/betslip-sbk/hooks/betslip-hooks';
import { useBetSlipMarketName } from '@/feature/betslip-sbk/hooks/use-bet-slip-market-name';
import { useSbkBetSlipStore } from '@/feature/betslip-sbk/hooks/use-sbk-betslip-store';
import { useIsPredictionsProduct } from '@/hooks/use-is-predictions-product';
import { useSplitText } from '@/hooks/use-split-text';
import { isAndroid } from '@/utils/constants-platform-specific';

import { EventMatchupText } from '../EventMatchupText';
import { EventTimeInfo } from '../EventTimeInfo';
import { OptionName } from '../OptionName';
import { SbkPromoWidget } from '../SbkPromoWidget';
import { BetInputs } from '../bet-inputs/BetInputs';
import { SinglesOdds } from './SinglesOdds';

const SUSPENDED_COLOR = 'neutral400' as const;

type SinglesCardProps = {
    selectionId: string;
    onItemLayout?: (selectionId: string, event: LayoutChangeEvent) => void;
};

export const SinglesCard = memo(({ selectionId, onItemLayout }: SinglesCardProps) => {
    const { t } = useTranslation('betslip_sbk');
    const isBetSlipSubmitting = useIsBetSlipSubmitting();
    const isSuspended = useIsOptionSuspended(selectionId);
    const removeSelection = useSbkBetSlipStore(state => state.actions.removeSelection);
    const eventId = useSbkBetSlipStore(state => state.selections[selectionId]?.eventId);
    const marketId = useSbkBetSlipStore(state => state.selections[selectionId]?.marketId);
    const marketName = useBetSlipMarketName({ marketId, eventId });
    const textColor = isSuspended ? SUSPENDED_COLOR : 'neutral300';
    const showBonusToggle = useShowBonusToggle();
    const isPredictions = useIsPredictionsProduct();

    const { Line1, Line2 } = useSplitText({ text: marketName, color: textColor });

    const handleDelete = () => {
        GASbkEvents.removeSelection();
        removeSelection(selectionId);
    };

    const handleLayout = useCallback(
        (e: LayoutChangeEvent) => onItemLayout?.(selectionId, e),
        [onItemLayout, selectionId]
    );

    const cardContent = (
        <Box
            borderRadius="r20"
            backgroundColor={ds.colorToken.background.neutral.subtler}
            p="s16"
            mb="s16"
            onLayout={handleLayout}
        >
            <InnerBorder borderRadius="r20" borderColor={ds.colorValue.border.subtle} />

            <Row>
                {isPredictions ? null : (
                    <Box mt="s4">
                        <TouchableOpacity hitSlop={8} onPress={handleDelete} disabled={isBetSlipSubmitting}>
                            <RemoveIcon height={16} width={16} />
                        </TouchableOpacity>
                    </Box>
                )}

                <Box flex={1} ml={isPredictions ? undefined : 's12'} mr="s12">
                    <Row alignItems="center">
                        <OptionName
                            variant="titleMedium"
                            optionId={selectionId}
                            color={isSuspended ? SUSPENDED_COLOR : ds.colorToken.text.primary}
                        />
                        <Box mx="s6">
                            <LineSeparator vertical style={styles.separator} />
                        </Box>
                        {Line1}
                        {isSuspended ? null : (
                            <Box ml="s8">
                                <SinglesOdds
                                    optionId={selectionId}
                                    variant="titleMedium"
                                    color={ds.colorToken.text.brand}
                                />
                            </Box>
                        )}
                    </Row>
                    {Line2}
                </Box>
            </Row>

            <Row mt="s8">
                <EventMatchupText eventId={eventId} color={isSuspended ? SUSPENDED_COLOR : 'neutral300'} />
                <EventTimeInfo
                    eventId={eventId}
                    color={isSuspended ? SUSPENDED_COLOR : 'neutral300'}
                    isSuspended={isSuspended}
                />
            </Row>

            {isSuspended ? (
                <Row
                    mt="s16"
                    px="s12"
                    py="s20"
                    backgroundColor={ds.colorToken.background.neutral.subtle}
                    borderRadius="r16"
                    justifyContent="center"
                    alignItems="center"
                >
                    <LockIcon />
                    <Text variant="titleMedium" ml="s8" color="neutral400">
                        {t('market_suspended')}
                    </Text>
                </Row>
            ) : (
                <>
                    <BetInputs betId={selectionId} />
                    {showBonusToggle ? <BonusToggleRow betId={selectionId} /> : null}
                </>
            )}
        </Box>
    );

    // On Android, use Pressable instead of TouchableWithoutFeedback to dismiss keyboard.
    // TouchableWithoutFeedback intercepts scroll gestures and prevents FlatList from scrolling.
    // Pressable only fires onPress on a completed tap, so it doesn't block scrolling.
    if (isAndroid) {
        return (
            <SbkPromoWidget betId={selectionId}>
                <Pressable onPress={Keyboard.dismiss} accessible={false}>
                    {cardContent}
                </Pressable>
            </SbkPromoWidget>
        );
    }

    return (
        <SbkPromoWidget betId={selectionId}>
            <TouchableWithoutFeedback onPress={Keyboard.dismiss} accessible={false}>
                {cardContent}
            </TouchableWithoutFeedback>
        </SbkPromoWidget>
    );
});

const styles = StyleSheet.create({
    separator: {
        height: 16,
    },
});
